import { useEffect, useState } from "react";
import { Activity } from "@features/admin/activity/types/activity";
import { fetchActivityAsync, fetchAssignedActivityAsync } from "@features/admin/activity/services/activitySvc";
import { ActivityQueryParams } from "@features/admin/activity/types/activityQueryParams";
import { normalizeAllForList } from "@core/utils/listQuery";

/**
 * React hook to load and manage paginated activity data for a given nation ID.
 *
 * This hook fetches activity data via an asynchronous helper (fetchActivityAsync) and
 * exposes pagination metadata, the activity list, a loading flag, and a reload function.
 * It performs an initial load and re-loads automatically whenever the input parameters
 * change (nationId, page or all).
 *
 * @remarks
 * - This is a React hook and must be called from a component or another hook.
 * - The hook sets `loading` to true while fetching and resets it to false when the
 *   fetch completes — even if the fetch throws. Errors thrown by the underlying
 *   fetchActivityAsync are not caught inside the hook and will propagate to the caller.
 * - State fields are populated from response.getData() if present; many fields may be
 *   undefined depending on the underlying API response.
 * - The hook's internal reload function can be called manually to refetch the current page.
 *
 * @param params - Object with parameters controlling the fetch:
 * @param params.nationId - Numeric nation identifier whose activities should be loaded.
 * @param params.page - Optional initial page number to request (defaults to 1 when not provided).
 * @param params.all - Optional flag to request all results (behavior depends on the backend).
 *
 * @returns An object containing the current data and pagination state:
 * - current_page: number — currently active page (defaults to 1).
 * - data: Activity[] — array of activity items (may be empty).
 * - first_page_url?: string — URL of the first page (if provided by the API).
 * - from?: number — index of the first item on the current page (if provided).
 * - last_page?: number — last available page number (if provided).
 * - last_page_url?: string — URL of the last page (if provided).
 * - links?: { url: string | null; label: string; active: boolean }[] — pagination link objects.
 * - next_page_url?: string | null — URL of the next page (if provided).
 * - path?: string — base path for pagination (if provided).
 * - per_page?: number — items per page (if provided).
 * - prev_page_url?: string | null — URL of the previous page (if provided).
 * - to?: number — index of the last item on the current page (if provided).
 * - total: number — total number of items available (defaults to 0).
 * - loading: boolean — true while a fetch is in progress.
 * - reload: () => Promise<void> — async function to manually re-fetch the current page.
 *
 */
export function useActivity(params: ActivityQueryParams, options: { enabled?: boolean } = { enabled: true }) {
    const [current_page, setCurrentPage] = useState(params.page || 1);
    const [data, setData] = useState<Activity[]>([]);
    const [first_page_url, setFirstPageUrl] = useState<string | undefined>(undefined);
    const [from, setFrom] = useState<number | undefined>(undefined);
    const [last_page, setLastPage] = useState<number | undefined>(undefined);
    const [last_page_url, setLastPageUrl] = useState<string | undefined>(undefined);
    const [links, setLinks] = useState<{ url: string | null; label: string; active: boolean }[] | undefined>(undefined);
    const [next_page_url, setNextPageUrl] = useState<string | null | undefined>(undefined);
    const [path, setPath] = useState<string | undefined>(undefined);
    const [per_page, setPerPage] = useState<number | undefined>(undefined);
    const [prev_page_url, setPrevPageUrl] = useState<string | null | undefined>(undefined);
    const [to, setTo] = useState<number | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const isEnabled = !!options?.enabled;

    // Function to reload activities
    const reload = async () => {
        // Guard first
        if (!isEnabled) {
            setLoading(false); // ensure it's not stuck
            return;
        }

        setLoading(true);

        try {
            const { nationId, ...rawFetchParams } = params;
            const fetchParams = normalizeAllForList(rawFetchParams, [
                "freetext",
                "activityName",
                "kpiName",
                "reportTemplateName",
                "description",
                "activityId",
                "reportTemplateId",
                "kpiId",
            ]);
            // Fetch activity data
            const response = await fetchActivityAsync(nationId, fetchParams);

            if (response) {
                // Update state with fetched data
                setCurrentPage(response.getData()?.current_page || 1);

                const rawData = response.getData()?.data || [];

                // If at least one item has list_order, sort by it (ascending).
                // Otherwise keep API order.
                const sortedData = rawData.some((x: any) => x.list_order != null)
                    ? rawData.slice().sort((a: any, b: any) => (a.list_order ?? 0) - (b.list_order ?? 0))
                    : rawData;

                setData(sortedData);

                setFirstPageUrl(response.getData()?.first_page_url);
                setFrom(response.getData()?.from);
                setLastPage(response.getData()?.last_page);
                setLastPageUrl(response.getData()?.last_page_url);
                setLinks(response.getData()?.links);
                setNextPageUrl(response.getData()?.next_page_url);
                setPath(response.getData()?.path);
                setPerPage(response.getData()?.per_page);
                setPrevPageUrl(response.getData()?.prev_page_url);
                setTo(response.getData()?.to);
                setTotal(response.getData()?.total || 0);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchActivityByTemplateId = async (reportTemplateId: number, departmentId: number): Promise<Activity[]> => {
        const response = await fetchAssignedActivityAsync(params.nationId, departmentId, reportTemplateId, {
            all: true,
        });

        return response.getData()?.data || [];
    };

    // Reload data when params change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); }, [
        params.nationId, params.page, params.all, params.freetext,
        params.activityName, params.kpiName, params.reportTemplateName,
        params.description, params.activityId, params.reportTemplateId,
        params.kpiId,
        options.enabled,
    ]);

    return {
        current_page,
        data,
        first_page_url,
        from,
        last_page,
        last_page_url,
        links,
        next_page_url,
        path,
        per_page,
        prev_page_url,
        to,
        total,
        loading,
        reload,
        fetchActivityByTemplateId,
    };
}